/**
 * Camera follow: while follow mode is on, re-center the active map on the
 * selected entity each time its position updates — keeping the current zoom.
 */

import { useEffect } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { useEntitySelectionStore } from '@/stores/entity-selection-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useCotStore } from '@/stores/cot-store';
import { getMarker } from '@/map/markers/marker-state';

/** Subscribe the camera to the selected entity while following. */
export function useFollowEntity(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    const recenter = (): void => recenterOnSelection(mapRef, modeRef);
    const unsubData = usePluginDataStore.subscribe(recenter);
    const unsubCot = useCotStore.subscribe(recenter);
    const unsubSelection = useEntitySelectionStore.subscribe(recenter);
    return () => {
      unsubData();
      unsubCot();
      unsubSelection();
    };
  }, []);
}

/** Recenter on the selected entity's latest position when following. */
function recenterOnSelection(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const { selectedUid, followMode } = useEntitySelectionStore.getState();
  if (!followMode || !selectedUid) return;
  const marker = getMarker(selectedUid);
  const map = mapRef.current;
  if (!marker || !map) return;
  centerMap(map, modeRef.current, marker.position);
}

/** Recenter the active map without changing zoom/range. */
function centerMap(map: MapInstance, mode: ViewMode, pos: google.maps.LatLngLiteral): void {
  if (mode === '2d') {
    (map as google.maps.Map).panTo(pos);
    return;
  }
  (map as google.maps.maps3d.Map3DElement).center = { lat: pos.lat, lng: pos.lng, altitude: 0 };
}
